/**
 * AI 消息格式转换工具
 * 用于将各种点击事件转换为标准格式 StandardAiComponentMessage
 */

import { formatDateTimeInChina, getChinaHour } from '../../../utils/dateChina';
import { StoreComponentClickParams, StoreComponentType } from '../types/aiAssistantTypes';

/**
 * 标准 AI 组件消息格式（根据 CompsAi.newDesign.md 设计）
 */
export interface StandardAiComponentMessage {
  msgId: string;
  source: {
    microServiceId: string;
    pageId: string;
    componentId: string;
    componentType: string;
  };
  eventData: {
    eventType: string;
    triggerTime: string;
    payload: Record<string, any>;
  };
  context?: {
    userId: string;
    token?: string;
  };
}

/**
 * 获取用户上下文信息
 */
function getUserContext(): { userId: string; token?: string } {
  // 从 localStorage 或 sessionStorage 获取用户信息
  const token =
    (typeof localStorage !== 'undefined' && localStorage.getItem('token')) ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('token')) ||
    undefined;

  // 从 token 或其他地方获取 userId（这里简化处理，实际应该从用户上下文获取）
  const userId = 'current-user'; // TODO: 从实际的用户上下文获取

  return { userId, token };
}

/**
 * 从 componentId 解析 componentType
 * 例如: "chart:revpar" -> "chart"
 */
function parseComponentType(componentId: string): string {
  const parts = componentId.split(':');
  return parts[0] || 'unknown';
}

/**
 * 组件类型到 componentId 的映射（与 useAiAssistant 的 COMPONENT_CONFIG 一致）
 */
const componentIdMap: Record<StoreComponentType | string, string> = {
  [StoreComponentType.ROOM_STATUS]: "table:room-status",
  [StoreComponentType.ORDER_TRAFFIC]: "chart:order-traffic",
  [StoreComponentType.ROOM_STATUS_RECENTLY]: "table:room-status-recently",
  [StoreComponentType.PRICE_ALERT]: "card:price-alert",
  [StoreComponentType.REVPAR]: "chart:revpar",
  [StoreComponentType.CHANNEL_INFO]: "chart:channel-info",
  [StoreComponentType.CHANNEL_PRICE]: "chart:channel-price",
  [StoreComponentType.ROOM_HEALTH]: "chart:room-health",
  [StoreComponentType.CUSTOMER_SOURCE]: "chart:customer-source",
  [StoreComponentType.DISCOUNT_ANALYSIS]: "chart:discount-analysis",
  [StoreComponentType.CHANNEL_ANALYSIS]: "chart:channel-analysis",
  [StoreComponentType.OCCUPANCY_ANALYSIS]: "chart:occupancy-analysis",
  [StoreComponentType.OPERATION_EFFICIENCY]: "chart:operation-efficiency",
  [StoreComponentType.STORE_OVERVIEW]: "card:store-overview",
};

/**
 * 从 StoreComponentType 获取 componentId
 */
function getComponentIdFromType(componentType: StoreComponentType | string): string {
  return componentIdMap[componentType] ||
    componentIdMap[String(componentType)] ||
    `unknown:${componentType}`;
}

/**
 * 生成消息唯一ID
 */
function generateMsgId(): string {
  // 使用 crypto.randomUUID() 或兼容的实现
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // 兼容实现
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * 将门店组件点击参数转换为标准 AI 组件消息格式
 * 
 * @param params - 门店组件点击参数
 * @param pageId - 页面ID（默认为 "store-revenue-monitor"）
 * @returns 标准格式的消息
 */
export function convertStoreComponentClickToStandard(
  params: StoreComponentClickParams,
  pageId: string = 'store-revenue-monitor'
): StandardAiComponentMessage {
  // 获取 componentId（优先使用 metadata.componentId，否则从 componentType 映射）
  const componentId = params.metadata?.componentId ||
    getComponentIdFromType(params.componentType);

  // 解析 componentType
  const componentType = parseComponentType(componentId);

  // 获取用户上下文
  const context = getUserContext();

  // 构建 payload（包含所有业务数据）
  const now = new Date();
  const payload: Record<string, any> = {
    storeId: params.storeId,
    componentType: params.componentType,
    data: params.data,
    metadata: params.metadata,
    clearPrevious: params.clearPrevious ?? true,
    // 当前时间（中国时区），供 AI 按时间规则判断价格调整（如 22 点及以后下调等）
    currentTime: now.toISOString(),
    currentTimeLocal: formatDateTimeInChina(now),
    currentHour: getChinaHour(now),
  };

  // 构建标准消息
  return {
    msgId: generateMsgId(),
    source: {
      microServiceId: 'mscentral',
      pageId,
      componentId,
      componentType,
    },
    eventData: {
      eventType: 'click',
      triggerTime: new Date().toISOString(),
      payload,
    },
    context: context.userId ? context : undefined,
  };
}

/**
 * 创建标准 AI 组件消息的通用函数
 * 
 * @param params - 消息参数
 * @returns 标准格式的消息
 */
export function createStandardAiMessage(params: {
  componentId: string;
  pageId: string;
  microServiceId?: string;
  componentType?: string;
  eventType?: string;
  payload: Record<string, any>;
  userId?: string;
  token?: string;
}): StandardAiComponentMessage {
  // 解析 componentType（如果未提供）
  const componentType = params.componentType ||
    parseComponentType(params.componentId);

  // 获取用户上下文（如果未提供）
  const context = params.userId ? {
    userId: params.userId,
    token: params.token,
  } : getUserContext();

  return {
    msgId: generateMsgId(),
    source: {
      microServiceId: params.microServiceId || 'mscentral',
      pageId: params.pageId,
      componentId: params.componentId,
      componentType,
    },
    eventData: {
      eventType: params.eventType || 'click',
      triggerTime: new Date().toISOString(),
      payload: params.payload,
    },
    context: context.userId ? context : undefined,
  };
}
